// Rapports — generation de rapports avec graphiques Recharts
import { useState, useCallback, useMemo } from "react";
import {
  ClipboardList,
  AlertCircle,
  CheckCircle,
  Download,
  FileText,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useReports, useGenerateReport, useRevenue } from "@/hooks/admin/useFinance";
import type {
  ReportTemplate,
  ReportType,
  RevenuePeriod,
} from "@/lib/admin-api/finance";

// Constants + formatters extracted to ./Reports/ during Maintainability
// Sprint 2026-04-19 (Task 1.2). See docs/superpowers/plans/.
import {
  TYPE_ICONS,
  CATEGORY_TABS,
  STATIC_FALLBACK_TEMPLATES,
  type ReportCategory,
} from "./Reports/constants";

// Chart sub-components extracted to ./Reports/ during Maintainability Sprint.
// ChartTooltip is used transitively via RevenueChartSection and OrdersLineChart.
import OrdersLineChart from "./Reports/OrdersLineChart";
import RevenueChartSection from "./Reports/RevenueChartSection";

// PeriodModal extracted to ./Reports/PeriodModal.tsx (Task 1.2 step 3)
import PeriodModal from "./Reports/PeriodModal";

// Recent reports table extracted to ./Reports/RecentReportsTable.tsx
// (Task 1.2 final squeeze).
import RecentReportsTable from "./Reports/RecentReportsTable";

// CSV helpers + date-range helper extracted to ./Reports/csv.ts (Task 1.2).
// `buildRevenueCsv` is re-exported below so existing vitest specs that do
// `import { buildRevenueCsv } from "../Reports"` continue to resolve.
import {
  computeRevenueDateRange,
  exportRevenueCsv,
  buildRevenueCsv,
} from "./Reports/csv";

// PDF export extracted to ./Reports/pdf.tsx (Task 1.2 squeeze). Kept in its
// own module so the dynamic @react-pdf/renderer import stays scoped.
import { exportRevenuePdf } from "./Reports/pdf";

export { buildRevenueCsv };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function Reports() {
  useAdminPageTitle("Rapports");
  const [activeCategory, setActiveCategory] = useState<ReportCategory>("ventes");
  const [selectedTemplate, setSelectedTemplate] =
    useState<ReportTemplate | null>(null);
  const [revenuePeriod, setRevenuePeriod] = useState<RevenuePeriod>("day");

  // Revenue query lifted here so CSV/PDF exports, the revenue chart, and the
  // orders line chart all share the same cached data.
  const { dateFrom, dateTo } = useMemo(
    () => computeRevenueDateRange(revenuePeriod),
    [revenuePeriod],
  );
  const revenueQuery = useRevenue(revenuePeriod, dateFrom, dateTo);

  const handleExportCsv = useCallback(() => {
    exportRevenueCsv(revenueQuery.data, revenuePeriod);
  }, [revenueQuery.data, revenuePeriod]);

  const handleExportPdf = useCallback(() => {
    void exportRevenuePdf(revenueQuery.data, revenuePeriod);
  }, [revenueQuery.data, revenuePeriod]);

  const { data, isLoading, isError, refetch } = useReports();
  const generateReport = useGenerateReport();

  const templates = data?.templates ?? (isError ? STATIC_FALLBACK_TEMPLATES : []);
  const recent = data?.recent ?? [];

  // Filter templates by category (map categories to report types)
  const categoryTypeMap: Record<ReportCategory, readonly ReportType[]> = {
    ventes: ["revenue"],
    produits: ["margin"],
    clients: ["revenue", "cashflow"],
    livraisons: ["expenses"],
    paiements: ["cashflow", "tva"],
  };

  const filteredTemplates = useMemo(
    () => {
      const types = categoryTypeMap[activeCategory];
      const filtered = templates.filter((t) => types.includes(t.type));
      // If no templates match the filter, show all (better UX than empty)
      return filtered.length > 0 ? filtered : templates;
    },
    [templates, activeCategory],
  );

  const handleGenerate = useCallback(
    async (templateId: string, period: string) => {
      await generateReport.mutateAsync({ templateId, period });
    },
    [generateReport],
  );

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Rapports
              </h1>
              <p className="text-sm text-stone-500">
                Generez et telechargez vos rapports d'activite
              </p>
            </div>
          </div>

          {/* Export buttons */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              className="gap-1.5"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              className="gap-1.5"
            >
              <FileText className="h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Report type selector tabs */}
        <div
          role="tablist"
          aria-label="Type de rapport"
          className="flex gap-1 overflow-x-auto rounded-lg border border-stone-200 bg-stone-50 p-1"
        >
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={activeCategory === tab.id}
              onClick={() => setActiveCategory(tab.id)}
              className={`flex shrink-0 items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeCategory === tab.id
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les rapports.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Reessayer
            </button>
          </div>
        )}

        {/* Mutation feedback */}
        {generateReport.isSuccess && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Rapport en cours de generation. Il apparaitra ci-dessous.</span>
          </div>
        )}

        {/* Charts section */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <RevenueChartSection
            period={revenuePeriod}
            onPeriodChange={setRevenuePeriod}
            data={revenueQuery.data}
            isLoading={revenueQuery.isLoading}
          />
          <OrdersLineChart />
        </div>

        {/* Templates — always shown, even when API errors (templates are static) */}
        <div>
          <h2 className="mb-4 text-sm font-semibold text-stone-700">
            Modeles disponibles
          </h2>
          {isLoading && !isError ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-lg border border-stone-200 bg-stone-100"
                />
              ))}
            </div>
          ) : filteredTemplates.length === 0 && !isError ? (
            <EmptyState
              icon={<ClipboardList className="h-6 w-6" />}
              title="Aucun modele disponible"
              description="Les modeles de rapports apparaitront ici."
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredTemplates.map((t) => (
                <div
                  key={t.id}
                  className="flex flex-col justify-between rounded-lg border border-stone-200 bg-white p-4 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                      {TYPE_ICONS[t.type]}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-stone-900">
                        {t.name}
                      </p>
                      <p className="mt-0.5 text-xs text-stone-500">
                        {t.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedTemplate(t)}
                      className="w-full gap-1.5 text-xs"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Generer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent reports — hidden on API error since data is unavailable */}
        {!isError && (
          <RecentReportsTable recent={recent} isLoading={isLoading} />
        )}
      </div>

      {/* Period picker modal */}
      <PeriodModal
        open={selectedTemplate !== null}
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onGenerate={handleGenerate}
        isGenerating={generateReport.isPending}
      />
    </AdminLayout>
  );
}
